import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import RegistrationModal from '../components/RegistrationModal';
import LoginModal from '../components/LoginModal';
import Leaderboard from '../components/Leaderboard';
import '../styles/MapPage.css';

// Component to render city name as clickable text on map
function CityLabelMarker({ city, onClick }) {
  const icon = useMemo(() => {
    return L.divIcon({
      className: 'city-label-marker',
      html: `<div class="city-label-text">${city.name}</div>`,
      iconSize: [null, null],
      iconAnchor: [0, 0],
    });
  }, [city.name]);

  return (
    <Marker
      position={city.position}
      icon={icon}
      eventHandlers={{
        click: () => onClick(city),
      }}
    />
  );
}

// Nominatim API endpoints (OpenStreetMap geocoding - free, no API key required)
const NOMINATIM_API_URL = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_REVERSE_API_URL = 'https://nominatim.openstreetmap.org/reverse';

// Component to handle map clicks
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: async (e) => {
      const { lat, lng } = e.latlng;
      
      try {
        // Reverse geocode the clicked location
        const response = await axios.get(NOMINATIM_REVERSE_API_URL, {
          params: {
            lat: lat,
            lon: lng,
            format: 'json',
            addressdetails: 1,
            'accept-language': 'en',
          },
          headers: {
            'User-Agent': 'Earthpeace App',
          },
        });

        const data = response.data;
        const address = data.address || {};
        
        // Build address string
        const addressParts = [];
        if (address.city) addressParts.push(address.city);
        else if (address.town) addressParts.push(address.town);
        else if (address.village) addressParts.push(address.village);
        else if (address.municipality) addressParts.push(address.municipality);
        else if (address.county) addressParts.push(address.county);
        
        if (address.state) addressParts.push(address.state);
        if (address.country) addressParts.push(address.country);

        const cityName = address.city || address.town || address.village || address.municipality || address.county || data.display_name.split(',')[0];
        const countryName = address.country || '';
        const fullAddress = data.display_name;

        const cityData = {
          id: data.place_id || Date.now(),
          name: cityName,
          address: addressParts.join(', ') || data.display_name, // Keep for backward compatibility
          fullAddress: fullAddress,
          city: cityName,
          country: countryName,
          position: [lat, lng],
        };

        onMapClick(cityData);
      } catch (error) {
        console.error('Error reverse geocoding:', error);
      }
    },
  });

  return null;
}

function MapPage() {
  const { authenticated, loading, hasActiveSubscription, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedCity, setSelectedCity] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const debounceTimer = useRef(null);

  // Redirect to dashboard if logged in and email verified (subscription is automatically active)
  useEffect(() => {
    if (!loading && authenticated && user?.email_verified && hasActiveSubscription) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, authenticated, hasActiveSubscription, user, navigate]);

  // Check for referral parameters and auto-open registration modal
  useEffect(() => {
    const refCode = searchParams.get('ref');
    const city = searchParams.get('city');
    const country = searchParams.get('country');
    const address = searchParams.get('address');

    if (refCode && city && country && address && !authenticated && !loading) {
      // Create city data from referral parameters
      const referralCityData = {
        id: `ref-${refCode}`,
        name: decodeURIComponent(city),
        city: decodeURIComponent(city),
        country: decodeURIComponent(country),
        fullAddress: decodeURIComponent(address),
        address: decodeURIComponent(address), // For backward compatibility
        position: [0, 0], // Default position, not used for referral
        referralCode: refCode,
      };

      setSelectedCity(referralCityData);
      setIsModalOpen(true);

      // Clean up URL parameters
      navigate('/', { replace: true });
    }
  }, [searchParams, authenticated, loading, navigate]);

  // Debounced search function
  useEffect(() => {
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Don't search if query is empty
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    // Set loading state
    setIsSearching(true);

    // Debounce the API call
    debounceTimer.current = setTimeout(async () => {
      try {
        const response = await axios.get(NOMINATIM_API_URL, {
          params: {
            q: searchQuery,
            format: 'json',
            limit: 10,
            addressdetails: 1,
            // Filter to prefer cities, towns, villages
            countrycodes: '', // Empty means all countries
            'accept-language': 'en',
          },
          headers: {
            'User-Agent': 'Earthpeace App', // Required by Nominatim
          },
        });

        // Filter results to prioritize cities, towns, villages, municipalities
        const cityTypes = ['city', 'town', 'village', 'municipality', 'administrative'];
        const filtered = response.data
          .filter((place) => {
            const type = place.type || '';
            const classType = place.class || '';
            return (
              cityTypes.includes(type) ||
              cityTypes.includes(classType) ||
              type === 'administrative'
            );
          })
          .map((place, index) => {
            // Build address string
            const address = place.address || {};
            const addressParts = [];
            if (address.city) addressParts.push(address.city);
            else if (address.town) addressParts.push(address.town);
            else if (address.village) addressParts.push(address.village);
            if (address.state) addressParts.push(address.state);
            if (address.country) addressParts.push(address.country);

            const cityName = address.city || address.town || address.village || place.display_name.split(',')[0];
            const countryName = address.country || '';

            return {
              id: place.place_id || index,
              name: cityName,
              address: addressParts.join(', ') || place.display_name, // Keep for backward compatibility
              fullAddress: place.display_name,
              city: cityName,
              country: countryName,
              position: [parseFloat(place.lat), parseFloat(place.lon)],
            };
          })
          .slice(0, 10); // Limit to 10 results

        setSearchResults(filtered);
      } catch (error) {
        console.error('Error searching cities:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms debounce delay

    // Cleanup function
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery]);

  const handleCityClick = (city) => {
    setSelectedCity(city);
    setIsModalOpen(true);
    setSearchQuery('');
    setIsSearchFocused(false);
  };

  const handleMapClick = (city) => {
    handleCityClick(city);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedCity(null);
  };

  const handleRegistrationSuccess = (email) => {
    setIsModalOpen(false);
    // Redirect to verify email page with email parameter
    window.location.href = `/verify-email?email=${encodeURIComponent(email)}`;
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchBlur = () => {
    // Delay to allow click events on city items
    setTimeout(() => {
      setIsSearchFocused(false);
    }, 200);
  };

  return (
    <div className="map-page">
      <header className="map-header">
        <div className="header-content">
          <p>Search or click on the map to select a city to get started</p>
          <button className="login-button" onClick={() => setIsLoginModalOpen(true)}>
            Login
          </button>
        </div>
      </header>
      <div className="map-container">
        <MapContainer
          center={[39.8283, -98.5795]}
          zoom={4}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <MapClickHandler onMapClick={handleMapClick} />
          {searchResults.map((city) => (
            <CityLabelMarker
              key={city.id}
              city={city}
              onClick={handleCityClick}
            />
          ))}
        </MapContainer>
        <div className="search-container">
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search for a city..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
            />
            {(searchQuery.trim() || isSearchFocused) && (
              <div className="search-results">
                {isSearching ? (
                  <div className="search-loading">Searching...</div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((city) => (
                    <div
                      key={city.id}
                      className="search-result-item"
                      onClick={() => handleCityClick(city)}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <div className="city-name">{city.name}</div>
                      <div className="city-address">{city.address}</div>
                    </div>
                  ))
                ) : searchQuery.trim() ? (
                  <div className="search-no-results">No cities found</div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
      {isModalOpen && selectedCity && (
        <RegistrationModal
          city={selectedCity}
          onClose={handleModalClose}
          onSuccess={handleRegistrationSuccess}
        />
      )}
      {isLoginModalOpen && (
        <LoginModal
          onClose={() => setIsLoginModalOpen(false)}
          onLoginRequest={(data) => {
            setIsLoginModalOpen(false);
            if (data.requiresVerification) {
              const emailToVerify = data.email || '';
              navigate(`/verify-email?email=${encodeURIComponent(emailToVerify)}`);
            } else if (data.requiresOTP || data.email) {
              // OTP sent, redirect to OTP verification screen
              const emailForOTP = data.email || '';
              navigate(`/login-otp?email=${encodeURIComponent(emailForOTP)}`);
            }
          }}
        />
      )}
      <Leaderboard />
    </div>
  );
}

export default MapPage;

